import { LoadingState } from "@repo/app-types/client/client";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Modal } from "@repo/ui/Modal/modal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { type FC, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { getAxiosInstanceWithAuth } from "../../../api/axiosConfig.js";
import {
  BalanceIncreaseOptionToValue,
  balanceIncreaseOptions,
} from "../../../helpers/transactionBalanceHelpers.js";
import { useAppDispatch } from "../../../hooks/hook.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";

interface GiftCardBalanceIncreaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddToBalance: () => void;
}

export const GiftCardBalanceIncreaseModal: FC<
  GiftCardBalanceIncreaseModalProps
> = ({ isOpen, onClose, onAddToBalance }) => {
  const dispatch = useAppDispatch();
  const [balanceIncreaseOption, setBalanceIncreaseOption] = useState<string>(
    balanceIncreaseOptions[4] ?? "",
  );
  const [increaseBalanceLoadingState, setIncreaseBalanceLoadingState] =
    useState<LoadingState>(LoadingState.LOADED);

  const onClickSubmit = async () => {
    setIncreaseBalanceLoadingState(LoadingState.LOADING);
    try {
      const axios = await getAxiosInstanceWithAuth();
      const value = BalanceIncreaseOptionToValue[balanceIncreaseOption] ?? 0;
      const unitAmount = value * 100;
      const { data } = await axios.post(`/billing/fund-balance`, {
        balance_transaction_id: uuidv4(),
        unit_amount: unitAmount,
      });

      showNotification(dispatch, {
        id: `balance-increased-${new Date().getTime()}`,
        primaryMessage: `$${value} added to balance`,
        type: NotificationType.SUCCESS,
      });

      // Inform the parent to reload balance data
      onAddToBalance();
      onClose();

      setIncreaseBalanceLoadingState(LoadingState.LOADED);
    } catch (err) {
      showNotification(dispatch, {
        id: `balance-increase-error-${new Date().getTime()}`,
        primaryMessage: `Sorry, an error occurred. Please try again.`,
        type: NotificationType.FAILURE,
      });

      onClose();

      setIncreaseBalanceLoadingState(LoadingState.ERROR);
    }
  };

  return (
    <Modal isOpen={isOpen} onCancel={onClose}>
      <ModalHeader onClose={onClose}>Add to gift card balance</ModalHeader>

      <div className="mt-6 mb-2 w-[120px]">
        <SimpleSelect
          label="Amount (USD)"
          onChange={setBalanceIncreaseOption}
          options={balanceIncreaseOptions.map((o) => ({ label: o, value: o }))}
          value={balanceIncreaseOption}
        />
      </div>
      <div className="text-sm text-muted-foreground">
        Your credit card will be charged and an invoice will be emailed to the
        billing contact.
      </div>
      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <div className="ml-3">
          <Button
            variant={ButtonVariant.Primary}
            isDisabled={increaseBalanceLoadingState === LoadingState.LOADING}
            onClick={() => onClickSubmit()}
            isLoading={increaseBalanceLoadingState === LoadingState.LOADING}
          >
            Add ${BalanceIncreaseOptionToValue[balanceIncreaseOption]} to
            balance
          </Button>
        </div>
        <Button variant={ButtonVariant.Secondary} onClick={() => onClose()}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
};
